import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getAuth, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import './AdminLogin.css';

const PhoneInputFormatter = (value) => {
  const digits = value.replaceAll(/[^\d]/g, '');
  
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
};

const AdminSignupPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success] = useState(false);
  const [consents, setConsents] = useState({
    smsEmail: false,
    privacyPolicy: false,
    termsOfService: false,
  });
  
  // Google sign-up completion state
  const [googleUser, setGoogleUser] = useState(null);
  const [googlePhone, setGooglePhone] = useState('');
  const [googleConsents, setGoogleConsents] = useState({
    smsEmail: false,
    privacyPolicy: false,
    termsOfService: false,
  });
  
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();

  const buildOnboardingRedirectPath = () => `/admin/onboarding${location.search || ''}`;

  // Only check auth state on mount (not realtime) to avoid conflicts during sign-in
  useEffect(() => {
    if (auth.currentUser && !googleUser) {
      navigate('/admin/dashboard', { replace: true });
    }
  }, []);

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePassword = (password) => {
    if (password.length < 6) return false;
    if (!/[A-Z]/.test(password)) return false;
    if (!/[a-z]/.test(password)) return false;
    if (!/[0-9]/.test(password)) return false;
    return true;
  };

  const handleChange = (field, value) => {
    if (field === 'phone') {
      value = PhoneInputFormatter(value);
    }
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const savePendingOtpSignup = (payload) => {
    const serialized = JSON.stringify(payload);
    localStorage.setItem('signupOtp.pending', serialized);
    sessionStorage.setItem('signupOtp.pending', serialized);
  };

  const handleGoogleSignUp = async () => {
    setError('');
    setLoading(true);
    
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope('email');
      provider.addScope('profile');
      
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      
      const db = getFirestore();
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (userDoc.exists()) {
        // User already exists - just navigate to dashboard
        navigate('/admin/dashboard');
        return;
      }
      
      // New user - show profile completion form
      setGoogleUser({
        uid: user.uid,
        name: user.displayName || '',
        email: user.email || '',
      });
    } catch (err) {
      console.error('Google sign-up error:', err);
      if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-up cancelled. Please try again.');
      } else if (err.code === 'auth/popup-blocked') {
        setError('Popup was blocked by your browser. Please allow popups for this site.');
      } else {
        setError(err.message || 'Google sign-up failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleProfileSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!googlePhone.trim() || googlePhone.replace(/[^\d]/g, '').length < 8) {
      setError('Please enter a valid phone number');
      return;
    }
    
    if (!googleConsents.privacyPolicy || !googleConsents.termsOfService) {
      setError('Please accept the Privacy Policy and Terms of Service to continue.');
      return;
    }
    
    setLoading(true);
    savePendingOtpSignup({
      provider: 'google',
      uid: googleUser.uid,
      name: googleUser.name,
      phone: googlePhone.trim(),
      email: googleUser.email.toLowerCase(),
      redirectPath: buildOnboardingRedirectPath(),
      consents: {
        smsEmail: googleConsents.smsEmail,
        privacyPolicy: googleConsents.privacyPolicy,
        termsOfService: googleConsents.termsOfService,
      },
      createdAt: new Date().toISOString(),
    });
    navigate(`/admin/signup/otp${location.search || ''}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim() || formData.name.trim().length < 2) {
      setError('Please enter a valid name (at least 2 characters)');
      return;
    }

    if (!/^[a-zA-Z\s]+$/.test(formData.name)) {
      setError('Name can only contain letters');
      return;
    }

    const phoneDigits = formData.phone.replaceAll(/[^\d]/g, '');
    if (phoneDigits.length < 8) {
      setError('Please enter a valid phone number');
      return;
    }

    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!validatePassword(formData.password)) {
      setError('Password must be at least 6 characters with 1 uppercase, 1 lowercase, and 1 number');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!consents.privacyPolicy || !consents.termsOfService) {
      setError('Please accept the Privacy Policy and Terms of Service to continue.');
      return;
    }

    setLoading(true);
    savePendingOtpSignup({
      provider: 'email',
      name: formData.name.trim(),
      phone: formData.phone.trim(),
      email: formData.email.trim().toLowerCase(),
      password: formData.password,
      redirectPath: buildOnboardingRedirectPath(),
      consents: {
        smsEmail: consents.smsEmail,
        privacyPolicy: consents.privacyPolicy,
        termsOfService: consents.termsOfService,
      },
      createdAt: new Date().toISOString(),
    });
    navigate(`/admin/signup/otp${location.search || ''}`);
  };

  // Success screen (used by both email and Google sign-up)
  if (success) {
    return (
      <div className="admin-login-page">
        <div className="ff-hero-ingredients">
          <div className="ff-ingredient tomato"></div>
          <div className="ff-ingredient pepper"></div>
          <div className="ff-ingredient mushroom"></div>
          <div className="ff-ingredient leaf"></div>
          <div className="ff-ingredient onion"></div>
          <div className="ff-ingredient carrot"></div>
          <div className="ff-ingredient garlic"></div>
          <div className="ff-ingredient broccoli"></div>
          <div className="ff-ingredient banana"></div>
          <div className="ff-ingredient bread"></div>
          <div className="ff-ingredient egg"></div>
          <div className="ff-ingredient sausage"></div>
          <div className="ff-ingredient cheese"></div>
          <div className="ff-ingredient lemon"></div>
          <div className="ff-ingredient strawberry"></div>
        </div>

        <div className="login-box">
          <div className="card card-primary card-outline">
            <div className="card-body login-card-body">
              <div className="text-center mb-4">
                <img src="/fodyfee_logo.png" alt="FodyFee" style={{ width: '80px', height: '80px' }} />
                <h3 className="login-heading">Account Created!</h3>
                <p className="login-subheading">Please check your email to verify your account</p>
              </div>

              <div className="row">
                <div className="col-12">
                  <Link to="/admin/login" className="btn btn-primary btn-block">
                    Back to Login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Google profile completion screen
  if (googleUser) {
    return (
      <div className="admin-login-page">
        <div className="ff-hero-ingredients">
          <div className="ff-ingredient tomato"></div>
          <div className="ff-ingredient pepper"></div>
          <div className="ff-ingredient mushroom"></div>
          <div className="ff-ingredient leaf"></div>
          <div className="ff-ingredient onion"></div>
          <div className="ff-ingredient carrot"></div>
          <div className="ff-ingredient garlic"></div>
          <div className="ff-ingredient broccoli"></div>
          <div className="ff-ingredient banana"></div>
          <div className="ff-ingredient bread"></div>
          <div className="ff-ingredient egg"></div>
          <div className="ff-ingredient sausage"></div>
          <div className="ff-ingredient cheese"></div>
          <div className="ff-ingredient lemon"></div>
          <div className="ff-ingredient strawberry"></div>
        </div>

        <div className="login-box">
          <div className="card card-primary card-outline">
            <div className="card-body login-card-body">
              <div className="text-center mb-4">
                <img src="/fodyfee_logo.png" alt="FodyFee" style={{ width: '80px', height: '80px' }} />
                <h3 className="login-heading">Complete Your Profile</h3>
                <p className="login-subheading">Signed in as {googleUser.email}</p>
              </div>

              {error && (
                <div className="alert alert-danger">
                  {error}
                </div>
              )}

              <form onSubmit={handleGoogleProfileSubmit}>
                <div className="form-group mb-3">
                  <label>Full Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={googleUser.name}
                    disabled
                  />
                </div>

                <div className="input-group mb-3">
                  <input
                    type="tel"
                    className="form-control"
                    placeholder="Phone Number (000) 000-0000"
                    value={googlePhone}
                    onChange={(e) => setGooglePhone(PhoneInputFormatter(e.target.value))}
                    autoComplete="tel"
                  />
                  <div className="input-group-append">
                    <div className="input-group-text">
                      <span className="fas fa-phone"></span>
                    </div>
                  </div>
                </div>

                <div className="form-group mb-3">
                  <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>Required Consents *</label>
                  <div className="form-check mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="googleSmsConsent"
                      checked={googleConsents.smsEmail}
                      onChange={(e) => setGoogleConsents(prev => ({ ...prev, smsEmail: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="googleSmsConsent" style={{ fontSize: '0.85rem' }}>
                      (Optional) SMS and Email Consent — By providing your phone number and email and checking this box, you agree to receive SMS notifications from FodyFee regarding order status, account security, service updates, and promotional/marketing offers. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe or HELP for assistance.
                    </label>
                  </div>
                  <div className="form-check mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="googlePrivacyConsent"
                      checked={googleConsents.privacyPolicy}
                      onChange={(e) => setGoogleConsents(prev => ({ ...prev, privacyPolicy: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="googlePrivacyConsent" style={{ fontSize: '0.85rem' }}>
                      I agree to the <a href="https://fodyfee.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                    </label>
                  </div>
                  <div className="form-check mb-2">
                    <input
                      type="checkbox"
                      className="form-check-input"
                      id="googleTermsConsent"
                      checked={googleConsents.termsOfService}
                      onChange={(e) => setGoogleConsents(prev => ({ ...prev, termsOfService: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="googleTermsConsent" style={{ fontSize: '0.85rem' }}>
                      I agree to the <a href="https://fodyfee.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                    </label>
                  </div>
                </div>

                <div className="row">
                  <div className="col-12">
                    <button 
                      type="submit" 
                      className="btn btn-primary btn-block"
                      disabled={loading}
                    >
                      {loading ? 'Creating Account...' : 'Complete Sign Up'}
                    </button>
                  </div>
                </div>
                
                <div className="row mt-3">
                  <div className="col-12 text-center">
                    <button 
                      type="button"
                      className="btn btn-link"
                      onClick={() => {
                        auth.signOut();
                        setGoogleUser(null);
                        setGooglePhone('');
                        setGoogleConsents({ smsEmail: false, privacyPolicy: false, termsOfService: false });
                      }}
                    >
                      Use a different account
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-login-page">
      {/* Ingredient sketches background */}
      <div className="ff-hero-ingredients">
        <div className="ff-ingredient tomato"></div>
        <div className="ff-ingredient pepper"></div>
        <div className="ff-ingredient mushroom"></div>
        <div className="ff-ingredient leaf"></div>
        <div className="ff-ingredient onion"></div>
        <div className="ff-ingredient carrot"></div>
        <div className="ff-ingredient garlic"></div>
        <div className="ff-ingredient broccoli"></div>
        <div className="ff-ingredient banana"></div>
        <div className="ff-ingredient bread"></div>
        <div className="ff-ingredient egg"></div>
        <div className="ff-ingredient sausage"></div>
        <div className="ff-ingredient cheese"></div>
        <div className="ff-ingredient lemon"></div>
        <div className="ff-ingredient strawberry"></div>
      </div>

      <div className="login-box">
        <div className="card card-primary card-outline">
          <div className="card-body login-card-body">
            <div className="text-center mb-4">
              <img src="/fodyfee_logo.png" alt="FodyFee" style={{ width: '80px', height: '80px' }} />
              <h3 className="login-heading">Create Account</h3>
              <p className="login-subheading">Sign up to get started</p>
            </div>

            {error && (
              <div className="alert alert-danger">
                {error}
              </div>
            )}

            {/* Google Sign Up Button */}
            <button
              type="button"
              className="btn btn-outline-dark btn-block mb-3"
              onClick={handleGoogleSignUp}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '10px',
                borderRadius: '8px',
                padding: '10px',
                fontWeight: 500,
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.64 9.2c0-.63-.06-1.25-.16-1.84H9v3.49h4.84c-.21 1.15-.84 2.12-1.79 2.77v2.31h2.9c1.69-1.56 2.69-3.86 2.69-6.73z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.31c-.8.54-1.83.86-3.06.86-2.36 0-4.36-1.59-5.07-3.74H.96v2.54C2.45 15.98 5.48 18 9 18z" fill="#34A853"/>
                <path d="M3.93 10.63c-.18-.54-.29-1.12-.29-1.72s.11-1.18.29-1.72V4.65H.96C.35 6.1 0 7.72 0 9.41c0 1.69.35 3.31.96 4.76l2.97-2.54z" fill="#FBBC05"/>
                <path d="M9 3.58c1.32 0 2.51.46 3.44 1.35l2.58-2.58C12.47.91 10.43 0 9 0 5.48 0 2.45 2.02.96 4.65l2.97 2.54c.71-2.15 2.71-3.74 5.07-3.74z" fill="#EA4335"/>
              </svg>
              {loading ? 'Signing in...' : 'Sign up with Google'}
            </button>

            <div className="text-center mb-3">
              <span style={{ color: '#999', fontSize: '0.85rem' }}>— or sign up with email —</span>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="input-group mb-3">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  autoComplete="name"
                />
                <div className="input-group-append">
                  <div className="input-group-text">
                    <span className="fas fa-user"></span>
                  </div>
                </div>
              </div>

              <div className="input-group mb-3">
                <input
                  type="tel"
                  className="form-control"
                  placeholder="Phone Number (000) 000-0000"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  autoComplete="tel"
                />
                <div className="input-group-append">
                  <div className="input-group-text">
                    <span className="fas fa-phone"></span>
                  </div>
                </div>
              </div>

              <div className="input-group mb-3">
                <input
                  type="email"
                  className="form-control"
                  placeholder="Email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  autoComplete="email"
                />
                <div className="input-group-append">
                  <div className="input-group-text">
                    <span className="fas fa-envelope"></span>
                  </div>
                </div>
              </div>

              <div className="input-group mb-3">
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="form-control"
                  placeholder="Password"
                  value={formData.password}
                  onChange={(e) => handleChange('password', e.target.value)}
                  autoComplete="new-password"
                />
                <div className="input-group-append">
                  <div className="input-group-text">
                    <span 
                      className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ cursor: 'pointer' }}
                    ></span>
                  </div>
                </div>
              </div>

              <div className="input-group mb-3">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  className="form-control"
                  placeholder="Confirm Password"
                  value={formData.confirmPassword}
                  onChange={(e) => handleChange('confirmPassword', e.target.value)}
                  autoComplete="new-password"
                />
                <div className="input-group-append">
                  <div className="input-group-text">
                    <span 
                      className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{ cursor: 'pointer' }}
                    ></span>
                  </div>
                </div>
              </div>

              <div className="form-group mb-3">
                <label style={{ fontWeight: 600, fontSize: '0.9rem' }}>Required Consents *</label>
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="smsConsent"
                    checked={consents.smsEmail}
                    onChange={(e) => setConsents(prev => ({ ...prev, smsEmail: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="smsConsent" style={{ fontSize: '0.85rem' }}>
                    (Optional) SMS and Email Consent — By providing your phone number and email and checking this box, you agree to receive SMS notifications from FodyFee regarding order status, account security, service updates, and promotional/marketing offers. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe or HELP for assistance.
                  </label>
                </div>
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="privacyConsent"
                    checked={consents.privacyPolicy}
                    onChange={(e) => setConsents(prev => ({ ...prev, privacyPolicy: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="privacyConsent" style={{ fontSize: '0.85rem' }}>
                    I agree to the <a href="https://fodyfee.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                  </label>
                </div>
                <div className="form-check mb-2">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="termsConsent"
                    checked={consents.termsOfService}
                    onChange={(e) => setConsents(prev => ({ ...prev, termsOfService: e.target.checked }))}
                  />
                  <label className="form-check-label" htmlFor="termsConsent" style={{ fontSize: '0.85rem' }}>
                    I agree to the <a href="https://fodyfee.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                  </label>
                </div>
              </div>

              <div className="row">
                <div className="col-12">
                  <button 
                    type="submit" 
                    className="btn btn-primary btn-block"
                    disabled={loading || !consents.privacyPolicy || !consents.termsOfService}
                  >
                    {loading ? 'Creating Account...' : 'Create Account'}
                  </button>
                </div>
              </div>
            </form>

            <div className="row mt-3">
              <div className="col-12 text-center">
                <p className="signup-link">
                  Already have an account?{' '}
                  <Link to="/admin/login" className="signup-link-a">
                    Sign in
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSignupPage;
